import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronLeft, ChevronRight, Lock, Mic, Minus, Plus, RotateCcw, X } from "lucide-react";
import { todayYmd, receiptDateToYmd } from "@shared/dates";
import { hasFullAccess } from "@shared/access";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ScanReceiptButton } from "@/components/ScanReceiptButton";
import { CurrencySelector, currencySymbol } from "@/components/CurrencySelector";
import VoiceCall from "@/components/VoiceCall";
import { apiFormRequest, apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";
import { warmAvatar, SELF_AVATAR } from "@/lib/avatar";
import { computeSplit, toCents, type SplitMode } from "@/lib/splitMath";
import { track } from "@/lib/analytics";
import { maybeAskForReview } from "@/lib/native-review";

// Add expense (Sept 2026 redesign). One sheet for groups and friends:
// AI first (talk or snap), then amount, what it was for, who paid, the spent
// date (posted date is locked and shown), who's in, how to split, and small
// extras. Every split mode works in whole cents so shares always add up.
// "They pay you / You pay them" is just who's ticked: untick yourself and
// they owe you the lot; pick them as payer and tick only you, you owe them.

export interface AddExpensePerson { id: string; name: string; isGhost?: boolean }

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const MON = MONTHS.map((m) => m.slice(0, 3));
const MODES: { key: SplitMode; label: string; hint: string }[] = [
  { key: "equal", label: "Equally", hint: "" },
  { key: "exact", label: "Exact", hint: "Type over anyone's amount. Everyone you haven't touched rebalances." },
  { key: "percent", label: "%", hint: "Set anyone's %. The rest share what's left." },
  { key: "shares", label: "Shares", hint: "More shares = pays more. A couple can be 2." },
];

const ymdToDate = (ymd: string) => { const [y, m, d] = ymd.split("-").map(Number); return new Date(y, m - 1, d); };
const dateToYmd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const errorText = (err: any, fallback: string) => {
  let msg = err?.message || fallback;
  try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error || msg; } catch {}
  return msg;
};

export function AddExpenseSheet({ open, onOpenChange, people: peopleIn, groupId, friendId, draft, invalidateKeys }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Group members, or [you, friend]. */
  people: AddExpensePerson[];
  groupId?: string;
  friendId?: string;
  /** Pre-fill (e.g. "Split this" from Personal Finance). */
  draft?: { description?: string; amount?: number } | null;
  /** Query keys to refresh after something is added. */
  invalidateKeys: unknown[][];
}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const me = user?.id ?? "";
  // You first, then everyone else in their usual order.
  const people = useMemo(() => [...peopleIn.filter((p) => p.id === me), ...peopleIn.filter((p) => p.id !== me)], [peopleIn, me]);
  const endpoint = groupId ? "/api/expenses" : "/api/friends/expenses";
  const context = groupId ? "group" : "friend";

  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  // Your home currency (picked once at sign-up) is what your plain amounts
  // have always been in, so the picker starts there and picking it converts
  // nothing. Another currency is converted into it.
  const home = (user?.defaultCurrency || "CAD").toUpperCase();
  const [currency, setCurrency] = useState(home);
  const [payerId, setPayerId] = useState(me);
  const [spentOn, setSpentOn] = useState(todayYmd());
  const [calOpen, setCalOpen] = useState(false);
  const [calView, setCalView] = useState(() => { const t = new Date(); return new Date(t.getFullYear(), t.getMonth(), 1); });
  const [excluded, setExcluded] = useState<string[]>([]);
  const [mode, setMode] = useState<SplitMode>("equal");
  const [exact, setExact] = useState<Record<string, number>>({});
  const [percent, setPercent] = useState<Record<string, number>>({});
  const [shares, setShares] = useState<Record<string, number>>({});
  const [editing, setEditing] = useState<{ id: string; raw: string } | null>(null);
  const [adjOn, setAdjOn] = useState(false);
  const [taxPct, setTaxPct] = useState("");
  const [tip, setTip] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState("");
  const [repeatOpen, setRepeatOpen] = useState(false);
  const [repeat, setRepeat] = useState<"off" | "weekly" | "monthly">("off");
  const [receipt, setReceipt] = useState<File | null>(null);
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false);

  // Fresh form every time the sheet opens.
  useEffect(() => {
    if (!open) return;
    setAmount(draft?.amount ? String(draft.amount) : "");
    setDescription(draft?.description ?? "");
    setCurrency(home); setPayerId(me); setSpentOn(todayYmd()); setCalOpen(false);
    setExcluded([]); setMode("equal"); setExact({}); setPercent({}); setShares({}); setEditing(null);
    setAdjOn(false); setTaxPct(""); setTip(""); setNoteOpen(false); setNote("");
    setRepeatOpen(false); setRepeat("off"); setReceipt(null); setPending(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!receipt) { setReceiptUrl(null); return; }
    const url = URL.createObjectURL(receipt);
    setReceiptUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [receipt]);

  const { data: ratesData } = useQuery<{ rates: Record<string, number> }>({
    queryKey: ["/api/exchange-rates"],
    enabled: open && hasFullAccess(user) && currency !== home,
    staleTime: 6 * 60 * 60 * 1000,
  });

  const sym = currency === "CAD" && home !== "CAD" ? "CA$" : currencySymbol(currency);
  const fmt = (cents: number) => `${sym}${(cents / 100).toFixed(2)}`;
  const nameOf = (id: string) => (id === me ? "You" : people.find((p) => p.id === id)?.name ?? "Someone");

  const baseCents = toCents(amount);
  const taxCents = adjOn ? Math.round(baseCents * (parseFloat(taxPct) || 0) / 100) : 0;
  const tipCents = adjOn ? toCents(tip) : 0;
  const totalCents = baseCents + taxCents + tipCents;
  const ids = people.map((p) => p.id).filter((id) => !excluded.includes(id));

  const split = useMemo(() => computeSplit({
    mode, ids, baseCents, totalCents, hasAdjustments: adjOn && (taxCents > 0 || tipCents > 0), exact, percent, shares,
  }), [mode, ids.join(","), baseCents, totalCents, adjOn, taxCents, tipCents, exact, percent, shares]);

  // Rates are quoted per 1 CAD; this is "1 unit of `currency` in `home`".
  const rate = currency === home ? 1
    : ratesData?.rates?.[currency] && ratesData?.rates?.[home] ? ratesData.rates[home] / ratesData.rates[currency] : undefined;
  const owedByOthers = split.rows.filter((r) => r.id !== payerId).reduce((a, r) => a + r.cents, 0);

  const blocker: string | null =
    !(baseCents > 0) ? "Enter an amount"
    : totalCents > 100_000_000 ? "That amount is too large"
    : !description.trim() ? "Add what it was for"
    : ids.length === 0 ? "Pick who it's split with"
    : ids.length === 1 && ids[0] === payerId ? "Add someone else"
    : mode !== "equal" && !split.ok ? split.message
    : owedByOthers <= 0 ? "Nobody else owes anything"
    : currency !== home && !rate ? "Can't get today's rate. Try again in a minute"
    : null;

  const allSame = split.rows.length > 1 && split.rows.every((r) => r.cents === split.rows[0].cents);
  const summary = blocker ? "" : allSame ? `· ${fmt(split.rows[0].cents)} each` : `· ${fmt(totalCents)}`;

  // Who owes whom, in words, for the two cases the old "They pay you /
  // You pay them" buttons covered.
  const payerIn = ids.includes(payerId);
  const others = ids.filter((id) => id !== payerId);
  const whoLine = !payerIn && others.length && baseCents > 0
    ? (payerId === me
        ? `You're not in this split, so ${others.map(nameOf).join(" & ")} ${others.length === 1 ? "owes" : "owe"} you the full ${fmt(totalCents)}.`
        : `${nameOf(payerId)} isn't in this split, so ${others.map((id) => (id === me ? "you" : nameOf(id))).join(" & ")} ${others.length === 1 && others[0] !== me ? "owes" : "owe"} ${nameOf(payerId)} the full ${fmt(totalCents)}.`)
    : null;

  const refresh = () => {
    invalidateKeys.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
    queryClient.invalidateQueries({ queryKey: ["/api/expenses"] });
    queryClient.invalidateQueries({ queryKey: ["/api/activity"] });
  };

  const toggle = (id: string) => {
    setExcluded((x) => (x.includes(id) ? x.filter((y) => y !== id) : [...x, id]));
    setExact(({ [id]: _, ...rest }) => rest);
    setPercent(({ [id]: _, ...rest }) => rest);
  };

  // ── spent date ──
  const today = ymdToDate(todayYmd());
  const spent = ymdToDate(spentOn);
  const daysAgo = Math.round((today.getTime() - spent.getTime()) / 864e5);
  const spentLabel = daysAgo === 0 ? "today" : daysAgo === 1 ? "yesterday"
    : `${MON[spent.getMonth()]} ${spent.getDate()}${spent.getFullYear() !== today.getFullYear() ? `, ${spent.getFullYear()}` : ""}`;
  const pickDate = (d: Date) => { setSpentOn(dateToYmd(d)); setCalOpen(false); };

  // ── repeat ──
  const nextRun = (() => {
    if (repeat === "off") return null;
    const n = ymdToDate(spentOn);
    const step = () => (repeat === "weekly" ? n.setDate(n.getDate() + 7) : n.setMonth(n.getMonth() + 1));
    step();
    while (n <= today) step();
    return n;
  })();

  const submit = async () => {
    if (blocker || pending) return;
    setPending(true);
    const isEqual = mode === "equal";
    const inSplit = isEqual ? ids : split.rows.filter((r) => r.cents > 0).map((r) => r.id);
    const splitAmounts = isEqual ? null
      : Object.fromEntries(split.rows.filter((r) => r.cents > 0).map((r) => [r.id, r.cents / 100]));
    const total = totalCents / 100;
    try {
      const fd = new FormData();
      fd.append("description", description.trim());
      fd.append("amount", String(total));
      fd.append("paidById", payerId);
      fd.append("splitAmongIds", JSON.stringify(inSplit));
      if (groupId) fd.append("groupId", groupId);
      fd.append("date", spentOn);
      if (splitAmounts) fd.append("splitAmounts", JSON.stringify(splitAmounts));
      if (note.trim()) fd.append("notes", note.trim());
      fd.append("currency", currency);
      if (receipt) fd.append("receipt", receipt);
      await apiFormRequest("POST", endpoint, fd);
    } catch (err) {
      setPending(false);
      toast({ title: "Couldn't add it", description: errorText(err, "Something went wrong"), variant: "destructive" });
      return;
    }
    let repeatFailed = false;
    if (repeat !== "off") {
      try {
        await apiRequest("POST", "/api/recurring", {
          description: description.trim(), amount: total, paidById: payerId, splitAmongIds: inSplit,
          groupId: groupId ?? null, frequency: repeat, splitAmounts, currency, notes: note.trim() || null,
          startDate: spentOn, firstCreated: true,
        });
        queryClient.invalidateQueries({ queryKey: ["/api/recurring"] });
      } catch { repeatFailed = true; }
    }
    track("expense_created", { context, split_type: mode, amount: total, currency, has_receipt: !!receipt });
    refresh();
    setPending(false);
    onOpenChange(false);
    toast(repeatFailed
      ? { title: "Expense added", description: "The repeat couldn't be set up. Try adding it again.", variant: "destructive" }
      : { title: repeat === "off" ? "Expense added" : "Expense added", description: repeat === "off" ? undefined : `Repeats ${repeat}.` });
    maybeAskForReview("expense_added");
  };

  // ── AI scan (same behaviour as before: creates the expense straight away) ──
  const scanMembers = people.map((p) => ({ id: p.id, name: p.id === me ? (user?.name || "You") : p.name }));
  const onItemSplit = async (splits: { description: string; amount: number; splitAmongIds: string[] }[], scanId: string | null, receiptDate?: string | null) => {
    const results = await Promise.allSettled(splits.map(async (s) => {
      const fd = new FormData();
      fd.append("description", s.description.trim());
      fd.append("amount", String(s.amount));
      fd.append("paidById", me);
      fd.append("splitAmongIds", JSON.stringify(s.splitAmongIds));
      if (groupId) fd.append("groupId", groupId);
      fd.append("date", receiptDateToYmd(receiptDate) ?? todayYmd());
      if (scanId) fd.append("aiScanId", scanId);
      return (await apiFormRequest("POST", endpoint, fd)).json();
    }));
    refresh();
    queryClient.invalidateQueries({ queryKey: ["/api/scan-receipt/quota"] });
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - ok;
    onOpenChange(false);
    if (failed === 0) toast({ title: `${ok} expense${ok !== 1 ? "s" : ""} added` });
    else if (ok === 0) toast({ title: "Couldn't create expenses", description: errorText((results.find((r) => r.status === "rejected") as PromiseRejectedResult)?.reason, "Couldn't create expenses"), variant: "destructive" });
    else toast({ title: `${ok} of ${results.length} expenses added`, description: `${failed} couldn't be created. Add those by hand.`, variant: "destructive" });
  };
  const onScanResult = async (data: { merchant: string; total: number | null; date?: string | null }, file: File, scanId: string | null) => {
    if (!user) return;
    try {
      const receiptYmd = receiptDateToYmd(data.date ?? null);
      const fd = new FormData();
      fd.append("description", data.merchant ? (data.date && !receiptYmd ? `${data.merchant} — ${data.date}` : data.merchant) : "Receipt");
      fd.append("amount", String(data.total ?? 0));
      fd.append("paidById", payerId || me);
      fd.append("splitAmongIds", JSON.stringify(ids.length ? ids : people.map((p) => p.id)));
      if (groupId) fd.append("groupId", groupId);
      fd.append("date", receiptYmd ?? todayYmd());
      if (scanId) fd.append("aiScanId", scanId);
      fd.append("receipt", file);
      await apiFormRequest("POST", endpoint, fd);
      refresh();
      queryClient.invalidateQueries({ queryKey: ["/api/scan-receipt/quota"] });
      onOpenChange(false);
      toast({ title: "Expense added" });
    } catch (err) {
      toast({ title: "Couldn't create expense", description: errorText(err, "Couldn't create expense"), variant: "destructive" });
    }
  };

  // ── calendar ──
  const cal = (() => {
    const y = calView.getFullYear(), m = calView.getMonth();
    const first = (new Date(y, m, 1).getDay() + 6) % 7;
    const days = new Date(y, m + 1, 0).getDate();
    const atNow = y === today.getFullYear() && m === today.getMonth();
    return { y, m, first, days, atNow };
  })();

  const chip = "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] transition-colors";
  const extraChip = (on: boolean) => `${chip} border ${on ? "bg-foreground text-background border-foreground" : "border-dashed border-border text-muted-foreground"}`;
  const label = "flex justify-between font-mono text-[11px] tracking-[0.1em] text-muted-foreground mt-4 mb-2 mx-0.5";

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" hideClose className="rounded-t-[28px] h-[92vh] max-h-[92vh] p-0 bg-sidebar border-0 flex flex-col gap-0" data-testid="add-expense-sheet">
          <div className="mx-auto mt-2 h-[5px] w-10 rounded-full bg-border shrink-0" />
          <div className="flex-1 overflow-y-auto px-[18px] pt-2.5 pb-3" style={{ overscrollBehavior: "contain" }}>
            <div className="flex items-center justify-between mb-1.5">
              <SheetTitle className="font-serif font-normal text-[26px] leading-none">New expense</SheetTitle>
              <button type="button" onClick={() => onOpenChange(false)} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center" aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <SheetDescription className="sr-only">Say it, snap a receipt, or type the expense.</SheetDescription>

            {/* AI first: talk or snap */}
            <div className="flex items-center gap-2 h-[52px] rounded-full bg-card border border-accent-foreground/35 pl-4 pr-1.5 shadow-[0_10px_24px_-14px_rgba(20,12,4,0.35)] mt-1.5 mb-3.5">
              <button type="button" className="flex-1 text-left font-serif italic text-[17px] text-muted-foreground truncate" onClick={() => { onOpenChange(false); setVoiceOn(true); }}>
                Say it or snap the receipt…
              </button>
              <button type="button" aria-label="Talk" onClick={() => { onOpenChange(false); setVoiceOn(true); }}
                className="w-10 h-10 shrink-0 rounded-full bg-card text-accent-foreground shadow-[0_2px_8px_-1px_rgba(20,12,4,0.16)] flex items-center justify-center">
                <Mic className="w-[18px] h-[18px]" />
              </button>
              <ScanReceiptButton compact members={scanMembers} onItemSplit={onItemSplit} onResult={onScanResult} />
            </div>
            <div className="font-mono text-[10.5px] tracking-[0.14em] text-muted-foreground text-center -mt-1 mb-2.5">OR TYPE IT</div>

            {/* Amount, what, who paid, when */}
            <div className="rounded-[22px] bg-card px-4 py-3.5 shadow-[0_14px_34px_-22px_rgba(41,38,36,0.45)]">
              <div className="flex items-center gap-1.5">
                <CurrencySelector chip value={currency} onChange={setCurrency} />
                <input
                  inputMode="decimal" placeholder="0.00" value={amount} aria-label="Amount" data-testid="input-expense-amount"
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1"))}
                  className="flex-1 min-w-0 bg-transparent font-mono text-[44px] font-medium leading-tight outline-none placeholder:text-muted-foreground/40"
                />
              </div>
              {currency !== home && baseCents > 0 && (
                <p className="font-mono text-[12.5px] text-muted-foreground mt-0.5">
                  {rate ? `≈ ${home === "CAD" ? "CA$" : currencySymbol(home)}${((totalCents / 100) * rate).toFixed(2)} will be recorded` : "Getting today's rate…"}
                </p>
              )}
              <input
                placeholder="What was it for?" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200}
                aria-label="What was it for" data-testid="input-expense-desc"
                className="w-full bg-transparent font-serif text-[24px] mt-1 outline-none placeholder:text-muted-foreground/60"
              />
              <div className="flex flex-wrap gap-1.5 mt-3">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" className={`${chip} bg-accent text-muted-foreground`} data-testid="select-paid-by">
                      Paid by <b className="font-semibold text-foreground">{payerId === me ? "you" : nameOf(payerId)}</b> ▾
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
                    {people.map((p) => (
                      <DropdownMenuItem key={p.id} onSelect={() => setPayerId(p.id)} className="gap-2">
                        <span className="w-6 h-6 rounded-full text-white text-[11px] flex items-center justify-center" style={{ background: p.id === me ? SELF_AVATAR : warmAvatar(p.id) }}>{p.name[0]?.toUpperCase()}</span>
                        <span className="flex-1">{p.id === me ? `${p.name} (you)` : p.name}</span>
                        {p.id === payerId && <Check className="w-4 h-4" />}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <button type="button" onClick={() => { setCalOpen((o) => !o); setCalView(new Date(spent.getFullYear(), spent.getMonth(), 1)); }}
                  className={`${chip} ${calOpen ? "bg-foreground text-background" : "bg-accent text-muted-foreground"}`} data-testid="spent-date-chip" aria-expanded={calOpen}>
                  Spent <b className={`font-semibold ${calOpen ? "" : "text-foreground"}`}>{spentLabel}</b> ▾
                </button>
              </div>

              {calOpen && (
                <div className="rounded-[18px] bg-background/60 dark:bg-muted/40 px-3 pt-2.5 pb-3 mt-2" data-testid="spent-calendar">
                  <div className="flex gap-1.5 mb-2.5">
                    {["Today", "Yesterday", "2 days ago"].map((t, i) => (
                      <button key={t} type="button" onClick={() => { const d = new Date(today); d.setDate(d.getDate() - i); pickDate(d); }}
                        className={`flex-1 rounded-full py-1.5 text-[12.5px] font-medium ${daysAgo === i ? "bg-foreground text-background" : "bg-accent text-muted-foreground"}`}>{t}</button>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mx-0.5 mb-1.5">
                    <button type="button" aria-label="Previous month" onClick={() => setCalView(new Date(cal.y, cal.m - 1, 1))} className="w-8 h-8 rounded-full bg-accent flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
                    <b className="font-serif font-normal text-[19px]">{MONTHS[cal.m]} {cal.y}</b>
                    <button type="button" aria-label="Next month" disabled={cal.atNow} onClick={() => setCalView(new Date(cal.y, cal.m + 1, 1))} className="w-8 h-8 rounded-full bg-accent flex items-center justify-center disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
                  </div>
                  <div className="grid grid-cols-7 gap-0.5 text-center">
                    {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => <span key={i} className="font-mono text-[10px] text-muted-foreground py-1">{d}</span>)}
                    {Array.from({ length: cal.first }, (_, i) => <span key={`b${i}`} />)}
                    {Array.from({ length: cal.days }, (_, i) => {
                      const d = new Date(cal.y, cal.m, i + 1);
                      const sel = dateToYmd(d) === spentOn, isToday = dateToYmd(d) === todayYmd();
                      return (
                        <button key={i} type="button" disabled={d > today} onClick={() => pickDate(d)} aria-label={`${MONTHS[cal.m]} ${i + 1}`}
                          className={`aspect-square rounded-full font-mono text-[13.5px] font-medium disabled:text-muted-foreground/40 ${sel ? "bg-accent-foreground text-white dark:bg-foreground dark:text-background" : isToday ? "ring-1 ring-inset ring-border" : ""}`}>
                          {i + 1}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[12px] leading-snug text-muted-foreground mt-2 mx-0.5">The day the money was actually spent. Future days are off; the posted date is always today.</p>
                </div>
              )}

              <div className={`flex items-center gap-2 mt-2.5 px-2.5 py-2 rounded-xl text-[12.5px] leading-snug transition-colors ${daysAgo > 0 ? "bg-accent text-accent-foreground" : "text-muted-foreground"}`} data-testid="posted-line">
                <Lock className="w-3 h-3 shrink-0" />
                <span>{daysAgo === 0 ? "Posted today · locked. Everyone sees both dates."
                  : `Spent ${spentLabel}, posted today · the posted date is locked, so everyone can see it was added ${daysAgo === 1 ? "a day" : `${daysAgo} days`} later.`}</span>
              </div>
            </div>

            {/* Who's in */}
            <div className={label}><span>SPLIT WITH</span><span>{ids.length} {ids.length === 1 ? "PERSON" : "PEOPLE"}</span></div>
            <div className="flex flex-wrap gap-2.5">
              {people.map((p) => {
                const on = !excluded.includes(p.id);
                return (
                  <button key={p.id} type="button" onClick={() => toggle(p.id)} aria-pressed={on} className="grid justify-items-center gap-1 text-[12px] text-muted-foreground w-[52px]">
                    <span className={`relative w-11 h-11 rounded-full flex items-center justify-center text-white font-serif text-[18px] transition ${on ? "" : "opacity-30 scale-90"}`}
                      style={{ background: p.isGhost ? "hsl(38 38% 45%)" : p.id === me ? SELF_AVATAR : warmAvatar(p.id) }}>
                      {p.name[0]?.toUpperCase()}
                      {on && <span className="absolute -right-0.5 -bottom-0.5 w-[17px] h-[17px] rounded-full bg-emerald-800 ring-2 ring-sidebar flex items-center justify-center"><Check className="w-2.5 h-2.5" strokeWidth={3} /></span>}
                    </span>
                    <span className="truncate max-w-full">{p.id === me ? "You" : p.name.split(" ")[0]}</span>
                  </button>
                );
              })}
            </div>
            {whoLine && <div className="mt-2 rounded-xl bg-accent px-2.5 py-2 text-[12.5px] leading-snug text-accent-foreground" data-testid="who-line">{whoLine}</div>}

            {/* How */}
            <div className={label}><span>HOW</span><span /></div>
            <div className="flex rounded-full bg-muted p-[3px] mb-2" role="tablist">
              {MODES.map((m) => (
                <button key={m.key} type="button" role="tab" aria-selected={mode === m.key} onClick={() => { setMode(m.key); setEditing(null); }}
                  className={`flex-1 rounded-full py-[7px] text-[12.5px] font-medium ${mode === m.key ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}
                  data-testid={`split-mode-${m.key}`}>{m.label}</button>
              ))}
            </div>
            {MODES.find((m) => m.key === mode)!.hint && <p className="text-[12.5px] leading-snug text-muted-foreground mx-0.5 mb-2">{MODES.find((m) => m.key === mode)!.hint}</p>}

            <div className="grid gap-1.5" data-testid="split-rows">
              {split.rows.map((r) => {
                const p = people.find((x) => x.id === r.id)!;
                const avatar = <span className="w-7 h-7 shrink-0 rounded-full text-white font-serif text-[14px] flex items-center justify-center" style={{ background: p.id === me ? SELF_AVATAR : warmAvatar(p.id) }}>{p.name[0]?.toUpperCase()}</span>;
                const extra = mode === "exact" && adjOn && (taxCents || tipCents) && r.value ? `+ ${fmt(r.cents - toCents(r.value))} tax & tip → ${fmt(r.cents)}`
                  : mode === "percent" ? fmt(r.cents)
                  : mode === "shares" ? `${r.count} of ${split.rows.reduce((a, x) => a + (x.count ?? 0), 0)} shares` : "";
                const nameCol = <span className="flex-1 min-w-0 grid leading-tight text-[14px]"><span className="truncate">{nameOf(r.id)}</span>{extra && <small className="text-[11.5px] text-muted-foreground">{extra}</small>}</span>;
                if (mode === "exact" || mode === "percent") {
                  const isEditing = editing?.id === r.id;
                  const store = mode === "exact" ? exact : percent;
                  return (
                    <div key={r.id} className={`flex items-center gap-2.5 rounded-[14px] bg-card py-2 pl-2 pr-2.5 ${r.set ? "shadow-[inset_3px_0_0_hsl(var(--accent-foreground))]" : ""}`} data-row={nameOf(r.id)}>
                      {avatar}{nameCol}
                      <label className="flex items-center h-[34px] rounded-[10px] bg-muted/60 px-2 font-mono text-[13px] text-muted-foreground">
                        {mode === "exact" && sym}
                        <input
                          inputMode="decimal" aria-label={`${nameOf(r.id)}'s ${mode === "exact" ? "amount" : "percent"}`}
                          // An auto row starts empty (its amount shows as the
                          // placeholder) so typing replaces it instead of adding
                          // digits to it; a row you already set keeps its value.
                          value={isEditing ? editing!.raw : r.value}
                          placeholder={r.value}
                          onFocus={(e) => { setEditing({ id: r.id, raw: r.set ? r.value ?? "" : "" }); if (r.set) requestAnimationFrame(() => e.target.select()); }}
                          onBlur={() => setEditing(null)}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/[^0-9.]/g, "");
                            setEditing({ id: r.id, raw });
                            const upd = (s: Record<string, number>) => {
                              const { [r.id]: _, ...rest } = s;
                              return raw === "" ? rest : { ...rest, [r.id]: mode === "exact" ? toCents(raw) : Math.max(0, parseFloat(raw) || 0) };
                            };
                            mode === "exact" ? setExact(upd) : setPercent(upd);
                          }}
                          className={`w-16 bg-transparent text-right font-mono text-[15px] outline-none placeholder:text-muted-foreground/70 ${r.set || isEditing ? "text-foreground" : "text-muted-foreground"}`}
                        />
                        {mode === "percent" && "%"}
                      </label>
                      <button type="button" aria-label="Back to auto" onClick={() => (mode === "exact" ? setExact : setPercent)(({ [r.id]: _, ...rest }) => rest)}
                        className={`w-[26px] h-[26px] shrink-0 rounded-full bg-accent text-accent-foreground flex items-center justify-center ${r.id in store ? "" : "invisible"}`}>
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                }
                return (
                  <div key={r.id} className="flex items-center gap-2.5 rounded-[14px] bg-card py-2 pl-2 pr-2.5" data-row={nameOf(r.id)}>
                    {avatar}{nameCol}
                    <b className="font-mono text-[15px] font-medium">{fmt(r.cents)}</b>
                    {mode === "shares" && (
                      <span className="flex items-center gap-1.5">
                        <button type="button" aria-label={`Fewer shares for ${nameOf(r.id)}`} onClick={() => setShares((s) => ({ ...s, [r.id]: Math.max(0, (s[r.id] ?? 1) - 1) }))} className="w-[30px] h-[30px] rounded-full bg-muted flex items-center justify-center"><Minus className="w-3.5 h-3.5" /></button>
                        <output className="min-w-[18px] text-center font-mono text-[15px]">{r.count}</output>
                        <button type="button" aria-label={`More shares for ${nameOf(r.id)}`} onClick={() => setShares((s) => ({ ...s, [r.id]: (s[r.id] ?? 1) + 1 }))} className="w-[30px] h-[30px] rounded-full bg-muted flex items-center justify-center"><Plus className="w-3.5 h-3.5" /></button>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            {mode !== "equal" && baseCents > 0 && ids.length > 0 && (
              <div className={`mt-2 flex items-center gap-2 rounded-xl px-3 py-2 text-[13px] font-medium ${split.ok ? "bg-muted/60 text-foreground" : "bg-destructive/10 text-destructive"}`} data-testid="split-status">
                <span>{split.message}</span>
                {!split.ok && (mode === "exact" || mode === "percent") && (
                  <button type="button" className="ml-auto underline text-[12.5px] font-semibold" onClick={() => (mode === "exact" ? setExact({}) : setPercent({}))}>Spread it evenly</button>
                )}
              </div>
            )}

            {/* Extras */}
            <div className={label}><span>EXTRAS</span><span /></div>
            <div className="flex flex-wrap gap-1.5">
              <button type="button" className={extraChip(adjOn)} onClick={() => setAdjOn((o) => !o)} data-testid="extra-tip-tax">
                {adjOn ? `✓ Tax ${taxPct || 0}%${tipCents ? ` · tip ${fmt(tipCents)}` : ""}` : "+ Tip & tax"}
              </button>
              <button type="button" className={extraChip(noteOpen || !!note.trim())} onClick={() => setNoteOpen((o) => (o && !note.trim() ? false : true))} data-testid="extra-note">
                {note.trim() ? "✓ Note" : "+ Note"}
              </button>
              <button type="button" className={extraChip(repeatOpen || repeat !== "off")} onClick={() => setRepeatOpen((o) => (o && repeat === "off" ? false : true))} data-testid="extra-repeat">
                ↻ {repeat === "off" ? "Repeat" : repeat === "weekly" ? "Weekly" : "Monthly"}
              </button>
              <label className={`${extraChip(!!receipt)} cursor-pointer`} data-testid="extra-receipt">
                📎 {receipt ? "Photo added" : "Receipt photo"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) setReceipt(f); e.target.value = ""; }} />
              </label>
            </div>

            {adjOn && (
              <div className="rounded-2xl bg-card px-3 py-2.5 mt-2 grid gap-2">
                <div className="flex gap-2">
                  <label className="flex-1 flex items-center justify-between h-[34px] rounded-[10px] bg-muted/60 px-2 font-mono text-[13px] text-muted-foreground">Tax %
                    <input inputMode="decimal" placeholder="13" value={taxPct} onChange={(e) => setTaxPct(e.target.value.replace(/[^0-9.]/g, ""))} aria-label="Tax percent" className="w-14 bg-transparent text-right text-[15px] text-foreground outline-none" />
                  </label>
                  <label className="flex-1 flex items-center justify-between h-[34px] rounded-[10px] bg-muted/60 px-2 font-mono text-[13px] text-muted-foreground">Tip {sym}
                    <input inputMode="decimal" placeholder="0.00" value={tip} onChange={(e) => setTip(e.target.value.replace(/[^0-9.]/g, ""))} aria-label="Tip amount" className="w-14 bg-transparent text-right text-[15px] text-foreground outline-none" />
                  </label>
                </div>
                <div className="grid gap-0.5 font-mono text-[12.5px] text-muted-foreground">
                  <div className="flex justify-between"><span>Subtotal</span><span>{fmt(baseCents)}</span></div>
                  <div className="flex justify-between"><span>Tax ({taxPct || 0}%)</span><span>{fmt(taxCents)}</span></div>
                  <div className="flex justify-between"><span>Tip</span><span>{fmt(tipCents)}</span></div>
                  <div className="flex justify-between border-t border-border pt-1 text-foreground font-medium"><span>Total</span><span>{fmt(totalCents)}</span></div>
                </div>
              </div>
            )}
            {(noteOpen || note.trim()) && (
              <div className="rounded-2xl bg-card px-3 py-2.5 mt-2 grid gap-2">
                <span className="font-mono text-[10.5px] tracking-[0.1em] text-muted-foreground">NOTE</span>
                <textarea rows={2} maxLength={500} autoFocus={!note} placeholder="e.g. split includes delivery fee" value={note} onChange={(e) => setNote(e.target.value)} aria-label="Note"
                  className="w-full resize-none rounded-[10px] bg-muted/60 px-2.5 py-2 text-[14px] outline-none" />
                <span className="text-[12px] text-muted-foreground">Everyone in the split sees it on the expense.</span>
              </div>
            )}
            {(repeatOpen || repeat !== "off") && (
              <div className="rounded-2xl bg-card px-3 py-2.5 mt-2 grid gap-2">
                <span className="font-mono text-[10.5px] tracking-[0.1em] text-muted-foreground">REPEAT</span>
                <div className="flex gap-1.5">
                  {(["off", "weekly", "monthly"] as const).map((r) => (
                    <button key={r} type="button" onClick={() => setRepeat(r)} className={`flex-1 rounded-full py-1.5 text-[12.5px] font-medium ${repeat === r ? "bg-foreground text-background" : "bg-accent text-muted-foreground"}`}>
                      {r === "off" ? "Off" : r === "weekly" ? "Weekly" : "Monthly"}
                    </button>
                  ))}
                </div>
                <span className="text-[12px] leading-snug text-muted-foreground">
                  {nextRun ? <>This one is added now. The next one is <b className="text-foreground font-semibold">{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][nextRun.getDay()]} {MON[nextRun.getMonth()]} {nextRun.getDate()}</b>, then every {repeat === "weekly" ? "week" : "month"}, with the same people, split, currency and note.</>
                    : "Adds it again every week or month, so rent and subscriptions split themselves."}
                </span>
              </div>
            )}
            {receipt && (
              <div className="rounded-2xl bg-card px-3 py-2.5 mt-2 grid gap-2">
                <span className="font-mono text-[10.5px] tracking-[0.1em] text-muted-foreground">RECEIPT PHOTO</span>
                <div className="flex items-center gap-2.5">
                  {receiptUrl && <img src={receiptUrl} alt="" className="w-[52px] h-[52px] rounded-[10px] object-cover bg-muted" />}
                  <span className="flex-1 min-w-0 truncate text-[13px]">{receipt.name}</span>
                  <button type="button" aria-label="Remove photo" onClick={() => setReceipt(null)} className="w-7 h-7 rounded-full bg-muted flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
                </div>
                <span className="text-[12px] text-muted-foreground">Everyone in the split gets a copy by email.</span>
              </div>
            )}
          </div>

          <div className="shrink-0 px-[18px] pt-2.5 pb-[max(22px,env(safe-area-inset-bottom))] bg-gradient-to-b from-transparent to-sidebar">
            <button type="button" onClick={submit} disabled={!!blocker || pending} data-testid="submit-expense"
              className="w-full h-[54px] rounded-full bg-foreground text-background font-semibold text-[16px] flex items-center justify-center gap-2 disabled:opacity-40">
              {pending ? "Adding…" : blocker ?? "Add expense"}
              {!pending && summary && <small className="font-mono text-[13px] font-medium opacity-75">{summary}</small>}
            </button>
          </div>
        </SheetContent>
      </Sheet>
      {voiceOn && <VoiceCall onClose={() => { setVoiceOn(false); refresh(); }} />}
    </>
  );
}
